import { Button, DatePicker, Form, Icons, Input, notification, Radio, Spin } from "@pankod/refine-antd";
import { useUpdate } from "@pankod/refine-core";
import dayjs from "dayjs";
import { IBackgroundScreening, IBaseProps, ICloudScreening, IScreeningMetadata, nextState } from "interfaces";
import { useEffect, useState } from "react";
import { DATAPROVIDER_UPDATE, RESOURCE_PATH } from "scripts/site";

export declare type BackgroundScreeningActionProps = {
    backgroundScreeningRecord: IBackgroundScreening;
    screeningMetadata: IScreeningMetadata;
    onFormSubmission: () => void;
    refetch: () => void;
    actions: nextState[] | undefined;
};
export const ManageBackgroundScreening: React.FC<BackgroundScreeningActionProps> = ({
    backgroundScreeningRecord, screeningMetadata, onFormSubmission, refetch, actions }) => {
    // Extend the form model locally to include transient UI field 'stateAction'
    type CloudScreeningForm = ICloudScreening & { stateAction?: string };
    const [form] = Form.useForm<CloudScreeningForm>();
    const { mutate } = useUpdate<IBaseProps>();
    const [savingForm, setSavingForm] = useState(false);
    const [selectedActionId, setSelectedActionId] = useState("");

    if (backgroundScreeningRecord?.screeningDate) {
        backgroundScreeningRecord.screeningDate = dayjs(backgroundScreeningRecord?.screeningDate);
    }
    // Reset form when record changes, then (re)apply single-action auto selection if applicable.
    // Previous implementation set the stateAction in one effect and then resetFields in another,
    // which cleared the value so it wasn't submitted. Combining them preserves the value.
    useEffect(() => {
        if (backgroundScreeningRecord) {
            form.resetFields();
            form.setFieldsValue({ stateAction: selectedActionId });
        }
    }, [backgroundScreeningRecord, form, selectedActionId]);

    useEffect(() => {
        if (actions?.length === 1) {
            const only = actions[0];
            if (only) {
                setSelectedActionId(only.action);
            }
        }
    }, [actions]);

    const saveForm = () => {
        setSavingForm(true);
        form.validateFields().then((values) => {
            mutate({
                dataProviderName: DATAPROVIDER_UPDATE,
                resource: RESOURCE_PATH.BACKGROUND_SCREENING,
                id: backgroundScreeningRecord?.id,
                values: values,
                successNotification: { key: "notificationKey", message: "Successful", description: `Successfully updated the request`, type: "success" },
                errorNotification: { key: "notificationKey", message: "Error occured", description: `Unable to update the request`, type: "error" }
            },
                {
                    onSuccess: () => {
                        onFormSubmission(); // Close the drawer
                        refetch(); // Refetch the data
                        setSavingForm(false); // Reset the submit button state 
                    },
                    onError: () => {
                        onFormSubmission(); // Close the drawer
                        setSavingForm(false); // Reset the submit button state
                    }
                }
            );
        }).catch((_errorInfo) => {
            notification.error({
                message: 'Incomplete Details',
                description:
                    'Please validate all the fields and save.'
            });
            setSavingForm(false);
            return;
        });
    }

    const selectedAction = actions?.find((act) => act.action === selectedActionId);   // Get the next state from selected action  
    const nextStateFromAction = screeningMetadata.states.find(st => st.state === selectedAction?.stateId);   // Get the next state from selected action
    const todaysDate = dayjs().add(1, "day"); // Add one day to today's date to prevent past dates

    useEffect(() => {
        if (selectedAction && selectedAction.predefinedComments) {
            form?.setFieldValue("comments", selectedAction.predefinedComments);
        }
    }, [selectedAction, form]);

    return (
        <Spin spinning={savingForm}>
            <Form
                layout="vertical"
                initialValues={{
                    ...backgroundScreeningRecord,
                    screeningType: "HireRight",
                    //stateAction: selectedActionId
                }}
                autoComplete="off"
                form={form}
            >
                {actions?.length === 1
                    ? <Form.Item name="stateAction" hidden={true}>
                        <Input />
                    </Form.Item>
                    : <Form.Item
                        label="Screening Request Status"
                        name="stateAction"
                        rules={[
                            {
                                required: true,
                                whitespace: true,
                            },
                        ]}
                    >
                        <Radio.Group
                            options={actions?.map((act) => ({
                                label: act.buttonText,    // Display text for the radio button
                                value: act.action,    // Value of the radio button    
                            }))}
                            optionType="button"
                            buttonStyle="solid"
                            onChange={(e) => {
                                setSelectedActionId(e.target.value);
                            }}
                        />
                    </Form.Item>
                }

                <Form.Item name="comments" hidden={true}>
                    <Input />
                </Form.Item>

                {nextStateFromAction?.fieldSetIds?.includes("screeningFields") &&
                    <>
                        <Form.Item
                            label="Screening Id"
                            name="screeningId"
                            tooltip={"HireRight screening Id"}

                            rules={[
                                {
                                    required: true,
                                    type: "string",
                                },
                                {
                                    validator: (_, value) => {
                                        const hireRightRegex = /^\w{2}-\d{6}-[A-Z0-9]{5}$/; // Regex for HireRight ID format without enforcing starting characters
                                        if (!value || hireRightRegex.test(value)) {
                                            return Promise.resolve();
                                        }
                                        return Promise.reject("Invalid HireRight Screening Id. Format: <2-character code>-<6-digit number>-<5-character alphanumeric code>");
                                    },
                                },
                            ]}
                        >
                            <Input placeholder={"AA-000000-AAAAA"} />
                        </Form.Item>

                        <Form.Item
                            label="Screening Date"
                            name="screeningDate"
                            tooltip="HireRight Screening completion date"
                            rules={[
                                {
                                    required: true,
                                    whitespace: true,
                                    type: "date"
                                },
                            ]}
                        >
                            <DatePicker
                                picker="date"
                                style={{ width: 280 }}
                                disabledDate={(selDate) => selDate && (selDate > todaysDate || selDate < dayjs().subtract(22, 'months'))}
                            // onChange={function (date) {
                            //     form.setFieldsValue({
                            //         readableScreeningDate: dayjs(date).format('DD-MMM-YYYY'),
                            //     });
                            // }}
                            />
                        </Form.Item>
                        {/* <Form.Item
                            hidden
                            name="readableScreeningDate"
                        >
                            <Input />
                        </Form.Item> */}
                        <Form.Item
                            hidden
                            label="Screening Type"
                            name="screeningType"
                        >
                            <Input />
                        </Form.Item>
                    </>
                }

                <Form.Item>
                    <Button
                        type="primary"
                        onClick={() => saveForm()}
                        style={{ backgroundColor: "green" }}
                        disabled={savingForm}
                        icon={<Icons.SaveOutlined />}
                    >
                        Save
                    </Button>
                </Form.Item>
            </Form>
        </Spin>
    );
}
